import { Box, Typography } from "@mui/material";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { formatCorrectPercentage } from "../../utils/helper";
import useParamValues from "../../utils/useParamValues";
import StudentCohortCard from "../../components/studentCohortCard/StudentCohortCard";
import clockIcon from "../../assets/clock_img.svg";
import percentageIcon from "../../assets/percentage.svg";
import soundWaveIcon from "../../assets/sound_wave.svg";
import useTranslationSetup from "../../utils/useTranslationSetup";
import StudentCohortResultBtn from "./studentCohortResultBtn/StudentCohortResultBtn";
import CohortAudioPlayer from "../../components/CohortAudioPlayer/CohortAudioPlayer";
import AnimatedGifText from "../../components/AnimatedGifText/AnimatedGifText";
import { MetricItem } from "../../components/CohortBottomDrawer/CohortMetricItem";
import { useCohortAudioPlayer } from "../../utils/useCohortAudioPlayer";
import {
  cohortDetailsMap,
  cohortIdMap,
  cohortStaticData,
  normalizeCohortId,
  UrlConstants,
  UNASSIGNED_COHORT_ID,
  UNASSIGNED_COHORT_KEY,
} from "../../utils/CommonUtils/constants";
import "./styles.css";
import CohortViewHeader from "../../components/CohortViewHeader/CohortViewHeader";
import CohortProgressComparison from "../../components/CohortProgressComparison/CohortProgressComparison";
import SubjectTag from "../../components/subjectTag/SubjectTag";
import { produceEventLog } from "../../utils/helper";
import { getUserProperties } from "../../utils/helper";

const StudentCohortResultScreen = ({
  assessmentSetResponse,
  onSelectAssessment,
}) => {
  const { t, tSubject } = useTranslationSetup();
  const { studentName, classNum, section } = useParamValues();
  // API sends -1, 1–4; -1 is the 5th (unassigned) cohort.
  const cohortNumber = normalizeCohortId(assessmentSetResponse.cohort);
  const cohortKey = cohortIdMap[cohortNumber] || UNASSIGNED_COHORT_KEY;
  const isUnassigned = cohortKey === UNASSIGNED_COHORT_KEY;

  const cohortContent =
    cohortStaticData[cohortNumber] || cohortStaticData[UNASSIGNED_COHORT_ID];
  const cohortDetails = cohortDetailsMap[cohortKey];

  const [isPlaying, playAudio, stopAudio, audioRef] = useCohortAudioPlayer(
    cohortContent?.audio
  );
  const location = useLocation();
  const navigate = useNavigate();
  const showBack = location.state?.showBack;
  const userProperties = getUserProperties(useParamValues());

  useEffect(() => {
    produceEventLog("results_2a_landing_page_view", {
      ...userProperties,
    });
  }, []);

  const primary_assessment_info =
    assessmentSetResponse?.assessments_result.find(
      (a) => a.assessment_type === "primary"
    );

  const secondary_assessment_info =
    assessmentSetResponse?.assessments_result.find(
      (a) => a.assessment_type === "secondary"
    );

  const previous_result_cohort =
    assessmentSetResponse?.previous_result?.previous_cohort == null
      ? assessmentSetResponse?.previous_result?.previous_cohort
      : normalizeCohortId(
          assessmentSetResponse.previous_result.previous_cohort
        );
  const previous_result_cwpm = assessmentSetResponse?.previous_result?.previous_cwpm;

  const grade = parseInt(classNum);
  const result = primary_assessment_info.result;
  let isWow = false;

  if (grade === 3 || grade === 4) {
    isWow = result["cwpm"] >= 50;
  } else if (grade === 6 || grade === 7) {
    isWow = result["cwpm"] >= 60;
  }

  const onClickStudentCohortResultBtn = (assessmentId, eventName) => {
    onSelectAssessment(assessmentId);
    produceEventLog(eventName, {
      ...userProperties,
    });
  };

  return (
    <>
      <Box className="cohort-main-screen-header-wrapper">
        <CohortViewHeader onBack={() => navigate(-1)} showBack={showBack} />
      </Box>
      <Box className="student-cohort-result-heading">
        <Typography className="student-cohort-result-student-name">
          {studentName}&apos;s
        </Typography>
        <Typography className="student-cohort-result-class-section">
          {t("class")} - {classNum}
          {section}
        </Typography>
      </Box>
      {UrlConstants.SHOW_COMPARISON && (
        <CohortProgressComparison
          previousCohort={previous_result_cohort}
          currentCohort={cohortNumber}
          previousCwpm={previous_result_cwpm}
          currentCwpm={secondary_assessment_info.result?.cwpm}
        />
      )}
      <Box className="student-cohort-result-assessment-header-row">
        <Typography className="student-cohort-result-assessment-header">
          {t("student_cohort_result_assessment_header_two", {
            assessmentNumber: 2,
          })}
        </Typography>
        <SubjectTag />
      </Box>
      <Box className={`${cohortContent.className}-bg-color`}>
        <Box
          className={`cohort-oval-color ${cohortContent?.className}-oval-color`}
        >
          <Typography className="cohort-title">
            {cohortContent.title}
          </Typography>
          <Box
            component="img"
            src={cohortContent.icon}
            alt={cohortContent.title}
            className="cohort-image"
          />
        </Box>
        <Box>
          {isUnassigned ? (
            <Typography className="cohort-gif-text">
              {cohortContent.cardDescription}
            </Typography>
          ) : (
            (cohortContent.descriptionKey || cohortContent.gifText) && (
              <Typography className="cohort-gif-text">
                {cohortContent.descriptionKey
                  ? tSubject(cohortContent.descriptionKey)
                  : cohortContent.gifText}
              </Typography>
            )
          )}
        </Box>
        {isUnassigned && cohortContent.infoBanner && (
          <Box className="cohort-info-banner-wrapper">
            <img
              src={cohortContent.infoBanner}
              alt="Information icon"
              className="cohort-info-banner-icon"
            />
            <Typography className="cohort-info-banner-text">
              {t("unassigned_cohort_reassessment_info")}
            </Typography>
          </Box>
        )}
        {!isUnassigned && (
          <Box className="cohort-media">
            <CohortAudioPlayer
              isPlaying={isPlaying}
              playAudio={playAudio}
              stopAudio={stopAudio}
              audioRef={audioRef}
              buttonStyle={{
                backgroundColor: "#A2C357",
                minWidth: 0,
                padding: "4px",
                borderRadius: "32px",
              }}
              iconStyle={{ color: "#FFFFFF", width: "20px", height: "20px" }}
            />
            {cohortContent.gifTextArray?.length > 0 && (
              <AnimatedGifText
                textArray={cohortContent.gifTextArray}
                pauseDuration={cohortContent.pauseDuration || 150}
                speed={cohortContent.gifSpeed}
              />
            )}
          </Box>
        )}

        {cohortDetails?.icon && cohortDetails?.iconNote && (
          <Box className="badge-details-box">
            <img
              src={cohortDetails.icon}
              alt="Level"
              className="cohort-note-icon"
            />
            <Typography className="cohort-card-note">
              {cohortDetails.iconNote}
            </Typography>
          </Box>
        )}

        <Box className="student-cohort-result-assessment-2-btn-container">
          <StudentCohortResultBtn
            assessmentNumber={2}
            onClick={() =>
              onClickStudentCohortResultBtn(
                secondary_assessment_info.id,
                "result_2a_assessment2_view_button"
              )
            }
          />
        </Box>
      </Box>
      <Typography className="student-cohort-result-assessment-header">
        {t("student_cohort_result_assessment_header_one", {
          assessmentNumber: 1,
        })}
      </Typography>
      <Box className="student-cohort-result-assessment-1-btn-container">
        <Box
          className={`assessment-btn-wrapper ${isWow ? "bg-wow" : "bg-practice"}`}
        >
          <StudentCohortResultBtn
            assessmentNumber={1}
            onClick={() =>
              onClickStudentCohortResultBtn(
                primary_assessment_info.id,
                "result_2a_assessment1_view_button"
              )
            }
          />
        </Box>
      </Box>
    </>
  );
};

export default StudentCohortResultScreen;
